import { ComponentType, FC, ReactNode } from 'react';
import {
  Package,
  Building2,
  Users,
  Linkedin,
  FileText,
  Target,
  MessageSquare,
  Calendar,
  CheckCircle2,
  Circle,
  ExternalLink,
  Edit2,
} from 'lucide-react';
import { Button } from '@/components/ui';
import type { WizardStatus } from '@/hooks/useWizard';

interface WizardConfigViewProps {
  wizardStatus: WizardStatus | null;
  loading: boolean;
  onEditStep: (stepNumber: number) => void;
}

interface StepCardProps {
  stepNumber: number;
  title: string;
  icon: ComponentType<{ className?: string }>;
  isCompleted: boolean;
  onEdit: () => void;
  children: ReactNode;
}

const StepCard: FC<StepCardProps> = ({
  stepNumber,
  title,
  icon: Icon,
  isCompleted,
  onEdit,
  children,
}) => {
  return (
    <div className="bg-white border border-gray-200 rounded-lg transition-all hover:border-gray-300">
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center justify-center w-8 h-8 rounded text-gray-400">
            <Icon className="w-4 h-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-400 tracking-wide uppercase">
                Step {stepNumber}
              </span>
              <span className="text-xs text-gray-300">/</span>
              <span className="text-xs font-medium text-gray-400 tracking-wide uppercase">
                {title}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            {isCompleted ? (
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-gray-500" />
                <span className="text-xs font-medium text-gray-600">Configured</span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <Circle className="w-3 h-3 text-gray-300" />
                <span className="text-xs font-medium text-gray-400">Pending</span>
              </div>
            )}
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={onEdit}
            className="text-xs font-medium"
          >
            <Edit2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
      <div className="p-6">
        {children}
      </div>
    </div>
  );
};

export const WizardConfigView: FC<WizardConfigViewProps> = ({
  wizardStatus,
  loading,
  onEditStep,
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-gray-700"></div>
          <p className="mt-3 text-sm text-gray-500 font-medium">Loading configuration...</p>
        </div>
      </div>
    );
  }

  if (!wizardStatus) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-16 text-center">
        <Package className="w-10 h-10 text-gray-300 mx-auto mb-4" />
        <h3 className="text-base font-semibold text-gray-900 mb-1">No Configuration Found</h3>
        <p className="text-sm text-gray-500 font-medium">
          Complete the onboarding wizard to configure your company setup.
        </p>
      </div>
    );
  }

  const data = wizardStatus.data || {};
  const completedSteps = calculateCompletedSteps(data, wizardStatus.wizardCompleted);
  const progressPercentage = (completedSteps / 8) * 100;

  return (
    <div className="space-y-6">
      {/* Progress Header */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Configuration Overview</h2>
              <p className="text-xs text-gray-500 mt-1 font-medium">
                {completedSteps} of 8 sections completed
              </p>
            </div>
            <div className="text-right">
              <div className="text-xl font-semibold text-gray-900">{Math.round(progressPercentage)}%</div>
              <div className="text-xs text-gray-500 font-medium">Progress</div>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 bg-gray-50">
          <div className="w-full bg-gray-200 rounded-sm h-1.5">
            <div
              className="bg-gray-700 h-1.5 rounded-sm transition-all duration-500"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>
      </div>

      {/* Step Cards */}
      <div className="space-y-3">
        {/* Step 1: Product/Service & Industry */}
        <StepCard
          stepNumber={1}
          title="Product/Service"
          icon={Package}
          isCompleted={!!(data.productOrService && data.industry)}
          onEdit={() => onEditStep(1)}
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Type</span>
                <p className="text-sm text-gray-900 mt-1.5 font-medium">{data.productOrService || 'N/A'}</p>
              </div>
              <div>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Industry</span>
                <p className="text-sm text-gray-900 mt-1.5 font-medium">{data.industry || 'N/A'}</p>
              </div>
            </div>
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Company Description</span>
              <p className="text-sm text-gray-700 mt-1.5 leading-relaxed line-clamp-3">{data.description || 'N/A'}</p>
            </div>
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Service Description</span>
              <p className="text-sm text-gray-700 mt-1.5 leading-relaxed line-clamp-3">{data.serviceDescription || 'N/A'}</p>
            </div>
          </div>
        </StepCard>

        {/* Step 2: Company Profile */}
        <StepCard
          stepNumber={2}
          title="Company Profile"
          icon={Building2}
          isCompleted={!!data.logoUrl || !!(data.keyPeople && data.keyPeople.length > 0)}
          onEdit={() => onEditStep(2)}
        >
          <div className="space-y-5">
            {/* Country & Timezone */}
            {(data.country || data.timezone) && (
              <div className="grid grid-cols-2 gap-4 pb-4 border-b border-gray-100">
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Country</span>
                  <p className="text-sm text-gray-900 mt-1.5 font-medium">{data.country || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Timezone</span>
                  <p className="text-sm text-gray-900 mt-1.5 font-medium">{data.timezone || 'N/A'}</p>
                </div>
              </div>
            )}
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 block">Logo</span>
              {data.logoUrl ? (
                <div className="w-20 h-20 border border-gray-200 rounded overflow-hidden bg-gray-50 flex items-center justify-center">
                  <img src={data.logoUrl} alt="Company Logo" className="max-w-full max-h-full object-contain" />
                </div>
              ) : (
                <p className="text-sm text-gray-400 font-medium">N/A</p>
              )}
            </div>
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3 block">
                Key People ({data.keyPeople?.length || 0})
              </span>
              {data.keyPeople && data.keyPeople.length > 0 ? (
                <div className="space-y-2.5">
                  {data.keyPeople.map((person, index) => (
                    <div key={index} className="flex items-center gap-3 py-2.5 border-b border-gray-100 last:border-0">
                      {person.pictureUrl ? (
                        <img
                          src={person.pictureUrl}
                          alt={`${person.name} ${person.familyName || ''}`}
                          className="w-9 h-9 rounded-full object-cover border border-gray-200"
                        />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center">
                          <Users className="w-4 h-4 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">
                          {person.name} {person.familyName || ''}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">{person.role || 'N/A'}</p>
                        {person.linkedinUrl ? (
                          <a
                            href={person.linkedinUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1 mt-1 font-medium"
                          >
                            LinkedIn <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : (
                          <p className="text-xs text-gray-400 mt-1">LinkedIn: N/A</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 font-medium">N/A</p>
              )}
            </div>
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3 block">Brand Colors</span>
              {data.brandColors && (data.brandColors.primary || data.brandColors.secondary || data.brandColors.accent) ? (
                <div className="flex gap-4">
                  <div className="flex items-center gap-2.5">
                    <div
                      className="w-10 h-10 rounded border border-gray-300 shadow-sm"
                      style={{ backgroundColor: data.brandColors.primary || '#f3f4f6' }}
                    />
                    <span className="text-xs text-gray-600 font-mono font-medium">{data.brandColors.primary || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <div
                      className="w-10 h-10 rounded border border-gray-300 shadow-sm"
                      style={{ backgroundColor: data.brandColors.secondary || '#f3f4f6' }}
                    />
                    <span className="text-xs text-gray-600 font-mono font-medium">{data.brandColors.secondary || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <div
                      className="w-10 h-10 rounded border border-gray-300 shadow-sm"
                      style={{ backgroundColor: data.brandColors.accent || '#f3f4f6' }}
                    />
                    <span className="text-xs text-gray-600 font-mono font-medium">{data.brandColors.accent || 'N/A'}</span>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-400 font-medium">N/A</p>
              )}
            </div>
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 block">Brand Guidelines</span>
              <p className="text-sm text-gray-700 leading-relaxed line-clamp-3">{data.brandGuidelines || 'N/A'}</p>
            </div>
          </div>
        </StepCard>

        {/* Step 3: Competitors */}
        <StepCard
          stepNumber={3}
          title="Competitors"
          icon={Users}
          isCompleted={!!(data.competitors && data.competitors.length > 0)}
          onEdit={() => onEditStep(3)}
        >
          {data.competitors && data.competitors.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {data.competitors.map((competitor) => (
                <span
                  key={competitor.id}
                  className="inline-flex items-center px-2.5 py-1.5 rounded text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200"
                >
                  {competitor.name}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 font-medium">N/A</p>
          )}
        </StepCard>

        {/* Step 4: LinkedIn */}
        <StepCard
          stepNumber={4}
          title="LinkedIn Company Page"
          icon={Linkedin}
          isCompleted={!!data.linkedinUrl}
          onEdit={() => onEditStep(4)}
        >
          {data.linkedinUrl ? (
            <a
              href={data.linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-gray-700 hover:text-gray-900 inline-flex items-center gap-2 font-medium group"
            >
              <span className="truncate max-w-md">{data.linkedinUrl}</span>
              <ExternalLink className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600 flex-shrink-0" />
            </a>
          ) : (
            <p className="text-sm text-gray-400 font-medium">N/A</p>
          )}
        </StepCard>

        {/* Step 5: Content Generation */}
        <StepCard
          stepNumber={5}
          title="Content Generation"
          icon={FileText}
          isCompleted={!!(data.contentTypes && data.contentTypes.length > 0)}
          onEdit={() => onEditStep(5)}
        >
          {data.contentTypes && data.contentTypes.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {data.contentTypes.map((type, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2.5 py-1.5 rounded text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200"
                >
                  {typeof type === 'object' ? type.category : type}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 font-medium">N/A</p>
          )}
        </StepCard>

        {/* Step 6: Sales Targets */}
        <StepCard
          stepNumber={6}
          title="Sales Targets"
          icon={Target}
          isCompleted={!!(data.salesTarget?.name && data.salesTarget?.industry && data.salesTarget?.role)}
          onEdit={() => onEditStep(6)}
        >
          <div className="space-y-4">
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Target Name</span>
              <p className="text-sm text-gray-900 mt-1.5 font-medium">{data.salesTarget?.name || 'N/A'}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Industry</span>
                <p className="text-sm text-gray-900 mt-1.5 font-medium">{data.salesTarget?.industry || 'N/A'}</p>
              </div>
              <div>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Target Role</span>
                <p className="text-sm text-gray-900 mt-1.5 font-medium">{data.salesTarget?.role || 'N/A'}</p>
              </div>
            </div>
          </div>
        </StepCard>

        {/* Step 7: Pitch Strategy */}
        <StepCard
          stepNumber={7}
          title="Pitch Strategy"
          icon={MessageSquare}
          isCompleted={!!data.pitchStrategy}
          onEdit={() => onEditStep(7)}
        >
          {data.pitchStrategy ? (
            <span className="inline-flex items-center px-3 py-1.5 rounded text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
              {data.pitchStrategy}
            </span>
          ) : (
            <p className="text-sm text-gray-400 font-medium">N/A</p>
          )}
        </StepCard>

        {/* Step 8: Calendly Link */}
        <StepCard
          stepNumber={8}
          title="Connect Calendar"
          icon={Calendar}
          isCompleted={true}
          onEdit={() => onEditStep(8)}
        >
          <p className="text-sm text-gray-700 font-medium flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            Manage your Google Calendar connection in detail view.
          </p>
        </StepCard>
      </div>
    </div>
  );
};

// Helper function to calculate completed steps
function calculateCompletedSteps(data: any, _wizardCompleted: boolean): number {
  if (!data) return 0;

  let completed = 0;

  // Step 1: Product/Service & Industry
  if (data.productOrService && data.industry) completed++;

  // Step 2: Company Profile (at least logo or key people)
  if (data.logoUrl || (data.keyPeople && data.keyPeople.length > 0)) completed++;

  // Step 3: Competitors
  if (data.competitors && data.competitors.length > 0) completed++;

  // Step 4: LinkedIn
  if (data.linkedinUrl) completed++;

  // Step 5: Content Generation
  if (data.contentTypes && data.contentTypes.length > 0) completed++;

  // Step 6: Sales Targets
  if (data.salesTarget?.name && data.salesTarget?.industry && data.salesTarget?.role) completed++;

  // Step 7: Pitch Strategy
  if (data.pitchStrategy) completed++;

  // Step 8: Connect Calendar (Always considered part of config)
  completed++;

  return completed;
}
